import { useEffect, useRef, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { createVendorInitiatedReversal, lookupOutboundPayout } from '../../../services/ReversalService'
import { formatAmount, formatPerson } from './formatting'

const EMPTY_FORM = {
    outbound_mpesa_receipt_number: '',
    inbound_mpesa_receipt_number:  '',
    inbound_amount:                '',
    inbound_transaction_time:      '',
    comment:                       '',
    reverse_platform_fee:          false,
    reverse_mpesa_fee:             false,
}

const STEPS = { FORM: 'form', CONFIRM: 'confirm' }

const bcSum = (parts) =>
    parts.reduce((acc, part) => acc + (Number(part) || 0), 0).toFixed(2)

// Converts the browser's naive "YYYY-MM-DDTHH:MM" datetime-local string into
// a proper ISO 8601 instant with the browser's UTC offset so the backend can
// parse it timezone-aware. Without the offset a Nairobi caller (UTC+3) would
// send a moment 3 hours in the local future that the backend would reject
// under before_or_equal:now (server time is UTC).
const toIsoLocal = (value) => {
    if (!value) return ''
    const asDate = new Date(value)
    if (Number.isNaN(asDate.getTime())) return ''
    return asDate.toISOString()
}

const CreateVendorReversalModal = ({ show, onHide }) => {
    const queryClient = useQueryClient()
    const [form, setForm]           = useState(EMPTY_FORM)
    const [step, setStep]           = useState(STEPS.FORM)
    const [lookup, setLookup]       = useState(null)
    const [lookupError, setLookupError] = useState(null)
    const [lookupLoading, setLookupLoading] = useState(false)
    // Monotonic request id so an in-flight lookup that resolves out of
    // order (typing "A" then "B" while the network is slow) cannot
    // overwrite the fresh state with a stale response. Every runLookup
    // bumps this ref, then only applies its response if the ref still
    // points at the same id when the promise settles.
    const lookupRequestId = useRef(0)

    useEffect(() => {
        if (!show) {
            setForm(EMPTY_FORM)
            setStep(STEPS.FORM)
            setLookup(null)
            setLookupError(null)
            setLookupLoading(false)
            lookupRequestId.current += 1
        }
    }, [show])

    // Debounced re-lookup on every change to the outbound receipt so the
    // preview panel and eligibility flags stay fresh without the user
    // needing to tab out of the field. Trailing 500ms of quiet after the
    // last keystroke fires the request; typing again cancels the pending
    // fire. Empty receipt clears the preview and cancels any pending run.
    useEffect(() => {
        const receipt = form.outbound_mpesa_receipt_number.trim()
        if (!receipt) {
            setLookup(null)
            setLookupError(null)
            return
        }
        const timerId = window.setTimeout(() => { runLookup(receipt) }, 500)
        return () => window.clearTimeout(timerId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.outbound_mpesa_receipt_number])

    const runLookup = async (receipt) => {
        const trimmed = receipt.trim()
        if (!trimmed) {
            setLookup(null)
            setLookupError(null)
            return
        }
        const requestId = ++lookupRequestId.current
        setLookupLoading(true)
        setLookupError(null)
        try {
            const response = await lookupOutboundPayout(trimmed)
            if (lookupRequestId.current !== requestId) return
            setLookup(response.data)
        } catch (err) {
            if (lookupRequestId.current !== requestId) return
            setLookup(null)
            const status = err?.response?.status
            if (status === 404) {
                setLookupError('No payout was found for that receipt.')
            } else if (status === 403) {
                setLookupError('You do not have permission to look up payouts.')
            } else {
                setLookupError(err?.response?.data?.message || 'Could not look up that receipt.')
            }
        } finally {
            if (lookupRequestId.current === requestId) {
                setLookupLoading(false)
            }
        }
    }

    const mutation = useMutation({
        mutationFn: (payload) => createVendorInitiatedReversal(payload),
        onSuccess: async () => {
            toast.success('Vendor refund recorded successfully.')
            await queryClient.invalidateQueries({ queryKey: ['reversals', 'vendor-initiated'] })
            onHide()
        },
        onError: (err) => {
            const message = err?.response?.data?.message || 'Failed to record vendor refund.'
            toast.error(message)
        },
    })

    const eligibilityIssues = (() => {
        if (!lookup) return []
        const issues = []
        if (!lookup.eligibility.is_settled) {
            issues.push('The payout has not been settled by Safaricom yet, so no refund can be booked against it.')
        }
        if (lookup.eligibility.already_reversed_by_daraja) {
            issues.push('This payout was already reversed by Safaricom, so no vendor refund can be recorded.')
        }
        if (Number(lookup.remaining_to_refund) <= 0) {
            issues.push('This payout has already been fully refunded through prior vendor refunds.')
        }
        return issues
    })()

    const canReversePlatformFee = lookup?.eligibility?.has_fee_snapshot ?? false
    const canReverseMpesaFee    = (lookup?.mpesa_fee_available ?? null) !== null

    useEffect(() => {
        if (form.reverse_platform_fee && !canReversePlatformFee) {
            setForm((f) => ({ ...f, reverse_platform_fee: false }))
        }
        if (form.reverse_mpesa_fee && !canReverseMpesaFee) {
            setForm((f) => ({ ...f, reverse_mpesa_fee: false }))
        }
    }, [canReversePlatformFee, canReverseMpesaFee, form.reverse_platform_fee, form.reverse_mpesa_fee])

    const formIsComplete =
        form.outbound_mpesa_receipt_number.trim() !== '' &&
        form.inbound_mpesa_receipt_number.trim() !== '' &&
        form.inbound_amount !== '' &&
        Number(form.inbound_amount) > 0 &&
        form.inbound_transaction_time.trim() !== '' &&
        form.comment.trim() !== ''

    const inboundAmountWithinRemaining =
        lookup && Number(form.inbound_amount) > 0 &&
        Number(form.inbound_amount) <= Number(lookup.remaining_to_refund)

    const canContinue =
        formIsComplete &&
        !!lookup &&
        eligibilityIssues.length === 0 &&
        inboundAmountWithinRemaining

    const platformFeeAmount = form.reverse_platform_fee ? Number(lookup?.platform_fee_available ?? 0) : 0
    const mpesaFeeAmount    = form.reverse_mpesa_fee    ? Number(lookup?.mpesa_fee_available ?? 0)    : 0
    const totalRefund       = bcSum([form.inbound_amount, platformFeeAmount, mpesaFeeAmount])

    const submit = () => {
        // Both amounts are required by the backend when the matching toggle
        // is on and rejected (as null / omitted) when it is off. Values come
        // from the lookup preview so the numbers booked exactly match what
        // the confirmation table showed the exec admin.
        const payload = {
            outbound_mpesa_receipt_number: form.outbound_mpesa_receipt_number.trim(),
            inbound_mpesa_receipt_number:  form.inbound_mpesa_receipt_number.trim(),
            inbound_amount:                Number(form.inbound_amount),
            inbound_transaction_time:      toIsoLocal(form.inbound_transaction_time),
            comment:                       form.comment.trim(),
            reverse_platform_fee:          form.reverse_platform_fee,
            reverse_mpesa_fee:             form.reverse_mpesa_fee,
        }
        if (form.reverse_platform_fee) {
            payload.platform_fee_refund_amount = Number(lookup?.platform_fee_available ?? 0)
        }
        if (form.reverse_mpesa_fee) {
            payload.mpesa_fee_refund_amount = Number(lookup?.mpesa_fee_available ?? 0)
        }
        mutation.mutate(payload)
    }

    const previewRows = lookup ? [
        {
            label:    `Wallet credit to ${formatPerson(lookup.source_user)}`,
            amount:   formatAmount(form.inbound_amount || 0),
            muted:    false,
        },
        {
            label:    'Platform fee refund',
            amount:   form.reverse_platform_fee ? formatAmount(lookup.platform_fee_available ?? 0) : 'Not included',
            muted:    !form.reverse_platform_fee,
        },
        {
            label:    'Mpesa fee refund',
            amount:   form.reverse_mpesa_fee ? formatAmount(lookup.mpesa_fee_available ?? 0) : 'Not included',
            muted:    !form.reverse_mpesa_fee,
        },
    ] : []

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            {step === STEPS.FORM && (
                <>
                    <Modal.Header closeButton>
                        <Modal.Title>Record vendor refund</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="text-muted mb-3">
                            The vendor has already sent the money back on M-Pesa. Enter what came in and match it to the original outbound payout so the ledger can catch up.
                        </p>
                        <form id="vendor-reversal-form" onSubmit={(e) => { e.preventDefault(); if (canContinue) setStep(STEPS.CONFIRM) }}>
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label className="form-label">Original Receipt ID <span className="text-danger">*</span></label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Receipt ID of the original payout"
                                            maxLength={20}
                                            value={form.outbound_mpesa_receipt_number}
                                            onChange={(e) => {
                                                setForm({ ...form, outbound_mpesa_receipt_number: e.target.value })
                                                setLookup(null)
                                                setLookupError(null)
                                            }}
                                            onBlur={(e) => runLookup(e.target.value)}
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => runLookup(form.outbound_mpesa_receipt_number)}
                                            disabled={lookupLoading || form.outbound_mpesa_receipt_number.trim() === ''}
                                        >
                                            Look up
                                        </button>
                                    </div>
                                    {lookupLoading && (
                                        <div className="small text-muted mt-1">
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                            Looking up payout...
                                        </div>
                                    )}
                                    {lookupError && (
                                        <div className="small text-danger mt-1">{lookupError}</div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Refund ID <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Receipt ID from the vendor's return"
                                        maxLength={20}
                                        value={form.inbound_mpesa_receipt_number}
                                        onChange={(e) => setForm({ ...form, inbound_mpesa_receipt_number: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            {lookup && (
                                <div className="border rounded p-3 mb-3 bg-light">
                                    <div className="fw-semibold mb-2">Payout summary</div>
                                    <table className="table table-sm mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                        <tbody>
                                            <tr>
                                                <th scope="row" className="text-muted fw-normal" style={{ width: '40%' }}>Recipient</th>
                                                <td style={{ wordBreak: 'break-word' }}>{formatPerson(lookup.source_user)}</td>
                                            </tr>
                                            <tr>
                                                <th scope="row" className="text-muted fw-normal">Original amount</th>
                                                <td>{formatAmount(lookup.payout_amount)}</td>
                                            </tr>
                                            <tr>
                                                <th scope="row" className="text-muted fw-normal">Already refunded</th>
                                                <td>{formatAmount(lookup.already_refunded_total)}</td>
                                            </tr>
                                            <tr>
                                                <th scope="row" className="fw-semibold">Remaining to refund</th>
                                                <td className="fw-semibold">{formatAmount(lookup.remaining_to_refund)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {lookup && (
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <div className="form-check">
                                            <input
                                                id="reverse-platform-fee"
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={form.reverse_platform_fee}
                                                disabled={!canReversePlatformFee}
                                                onChange={(e) => setForm({ ...form, reverse_platform_fee: e.target.checked })}
                                            />
                                            <label className="form-check-label" htmlFor="reverse-platform-fee">
                                                Also refund the platform fee
                                                {canReversePlatformFee && (
                                                    <span className="text-muted ms-1">({formatAmount(lookup.platform_fee_available)})</span>
                                                )}
                                            </label>
                                            {!canReversePlatformFee && (
                                                <div className="small text-muted">No fee snapshot on this payout, so no platform fee to refund.</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-check">
                                            <input
                                                id="reverse-mpesa-fee"
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={form.reverse_mpesa_fee}
                                                disabled={!canReverseMpesaFee}
                                                onChange={(e) => setForm({ ...form, reverse_mpesa_fee: e.target.checked })}
                                            />
                                            <label className="form-check-label" htmlFor="reverse-mpesa-fee">
                                                Also refund the Mpesa fee
                                                {canReverseMpesaFee && (
                                                    <span className="text-muted ms-1">({formatAmount(lookup.mpesa_fee_available)})</span>
                                                )}
                                            </label>
                                            {!canReverseMpesaFee && (
                                                <div className="small text-muted">No recorded Mpesa fee on this payout to refund.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {eligibilityIssues.length > 0 && (
                                <div className="alert alert-danger py-2 mb-3" role="alert">
                                    <ul className="mb-0 ps-3">
                                        {eligibilityIssues.map((issue, i) => <li key={i}>{issue}</li>)}
                                    </ul>
                                </div>
                            )}

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label">Inbound amount (KES) <span className="text-danger">*</span></label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Amount the vendor returned"
                                        min="0.01"
                                        step="0.01"
                                        max={lookup?.remaining_to_refund}
                                        value={form.inbound_amount}
                                        onChange={(e) => setForm({ ...form, inbound_amount: e.target.value })}
                                        required
                                    />
                                    {lookup && form.inbound_amount !== '' && !inboundAmountWithinRemaining && (
                                        <div className="small text-danger mt-1">
                                            Amount is greater than what remains on the payout.
                                        </div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Refund time <span className="text-danger">*</span></label>
                                    <input
                                        type="datetime-local"
                                        className="form-control"
                                        value={form.inbound_transaction_time}
                                        onChange={(e) => setForm({ ...form, inbound_transaction_time: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label">Comment <span className="text-danger">*</span></label>
                                    <textarea
                                        className="form-control"
                                        rows={2}
                                        placeholder="Short note on why this refund is being recorded"
                                        maxLength={255}
                                        value={form.comment}
                                        onChange={(e) => setForm({ ...form, comment: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>
                        </form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={onHide}>Cancel</Button>
                        <Button
                            type="submit"
                            form="vendor-reversal-form"
                            variant="primary"
                            disabled={!canContinue}
                        >
                            Continue
                        </Button>
                    </Modal.Footer>
                </>
            )}

            {step === STEPS.CONFIRM && lookup && (
                <>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm vendor refund</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="mb-3">
                            Once you record this, the ledger will book the entries below. This cannot be undone from the UI.
                        </p>
                        <div className="border rounded mb-3">
                            <table className="table mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th scope="col" style={{ width: '65%' }}>What will happen</th>
                                        <th scope="col" style={{ width: '35%' }} className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewRows.map((row, i) => (
                                        <tr key={i} className={row.muted ? 'text-muted' : ''}>
                                            <td style={{ wordBreak: 'break-word' }}>{row.label}</td>
                                            <td className="text-end" style={{ wordBreak: 'break-word' }}>{row.amount}</td>
                                        </tr>
                                    ))}
                                    <tr className="fw-semibold">
                                        <td>Total booked to the ledger</td>
                                        <td className="text-end">{formatAmount(totalRefund)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="small text-muted">
                            <div><strong>Refund ID:</strong> {form.inbound_mpesa_receipt_number}</div>
                            <div><strong>Comment:</strong> {form.comment}</div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => setStep(STEPS.FORM)}
                            disabled={mutation.isPending}
                        >
                            Back
                        </Button>
                        <Button
                            variant="primary"
                            onClick={submit}
                            disabled={mutation.isPending}
                        >
                            {mutation.isPending ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                    Recording
                                </>
                            ) : (
                                'Record refund'
                            )}
                        </Button>
                    </Modal.Footer>
                </>
            )}
        </Modal>
    )
}

export default CreateVendorReversalModal
